import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { appMode, pb } from "@/lib/pocketbase";
import { adoptAccountBusiness } from "@/lib/business";
import { adoptAccountCalendars } from "@/lib/calendarImport";
import { adoptAccountTheme } from "@/lib/theme";
import type { UserRecord } from "@/lib/types";

interface AuthValue {
  user: UserRecord | null;
  loading: boolean;
  loginWithPassword: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthValue | null>(null);

/**
 * The signed-in user, or null.
 *
 * `isValid` is not optional here. PocketBase restores `authStore.model` from
 * localStorage whether or not the token has expired, so reading `model` alone
 * leaves the app certain you are signed in while every request comes back 401.
 * The symptom is not an error: it is a fully rendered app with empty lists and
 * no sign-in form anywhere, because nothing ever thought to show one. That is
 * most likely to bite on a phone, which goes a fortnight between visits while
 * the laptop keeps its token warm.
 */
function currentUser(): UserRecord | null {
  return pb.authStore.isValid ? ((pb.authStore.model as unknown as UserRecord) ?? null) : null;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserRecord | null>(currentUser);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Keep React state in sync with the PocketBase auth store.
    return pb.authStore.onChange(() => {
      const next = currentUser();
      setUser(next);
      // The account's appearance is the durable copy — see adoptAccountTheme.
      adoptAccountTheme(next as { theme?: string; mode?: string } | null);
      adoptAccountCalendars(next as unknown as Record<string, unknown> | null);
      adoptAccountBusiness(next as unknown as Record<string, unknown> | null);
    });
  }, []);

  /**
   * A token can also be dead on the server — password changed, record deleted —
   * while still looking valid here, which produces the same silent empty app.
   * One refresh at boot settles it, and rolls the expiry forward so returning
   * after a long gap keeps you signed in rather than quietly failing.
   */
  useEffect(() => {
    if (appMode === "demo" || !pb.authStore.isValid) return;
    void pb.collection("users").authRefresh().catch((err: unknown) => {
      // Only a refusal means the token is dead. A phone that opened the app in
      // a lift has a network error, not an expired session, and clearing on
      // that would sign you out for being underground.
      const status = (err as { status?: number } | null)?.status;
      if (status === 401 || status === 403) pb.authStore.clear();
    });
  }, []);

  const value = useMemo<AuthValue>(
    () => ({
      user,
      loading,
      async loginWithPassword(email, password) {
        setLoading(true);
        try {
          await pb.collection("users").authWithPassword(email, password);
        } finally {
          setLoading(false);
        }
      },
      logout() {
        pb.authStore.clear();
      },
    }),
    [user, loading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
